import React, { useEffect } from 'react';
import cx from 'classnames';
import { Icon } from '@THC/components/Icon';
import type SwiperInstance from 'swiper';

import { SNOWPLOW_EVENTS } from 'Services/analytics/analytics.const';
import { motorwayMovePageEvents } from 'Services/analytics/events/motorwayMoveEvents';

import { ReactComponent as QuoteIcon } from './quote.svg';
import reviews from './Testimonials.json';
import type { TestimonialsCarouselProps } from './TestimonialsCarousel.types';

import styles from './TestimonialsCarousel.module.scss';

export const TestimonialsCarousel = (props: TestimonialsCarouselProps): React.ReactElement => {
	const swiperInstanceRef = React.useRef<unknown>();

	useEffect(() => {
		const swiperRootClass = `.swiper-container.${styles.carousel}`;

		const initSwiper = async () => {
			const { Swiper } = await import(/* webpackChunkName: "swiper" */ 'swiper');
			const { Autoplay, Pagination } = await import(/* webpackChunkName: "swiper" */ 'swiper/modules');

			swiperInstanceRef.current = new Swiper(swiperRootClass, {
				autoplay: {
					delay: 15000,
				},
				loop: true,
				modules: [Autoplay, Pagination],
				pagination: {
					clickable: true,
					dynamicBullets: true,
					el: `${swiperRootClass} .swiper-pagination`,
					type: 'bullets',
				},
				slidesPerView: 1,
				spaceBetween: 24,
				updateOnWindowResize: true,
			});

			(swiperInstanceRef.current as SwiperInstance).on('slideChange', () => {
				motorwayMovePageEvents({
					name: 'Move testimonial clicked',
					schemaName: SNOWPLOW_EVENTS.CLICK.SCHEMA,
					version: SNOWPLOW_EVENTS.CLICK.VERSION,
				});
			});
		};

		initSwiper().catch((e) => {
			// eslint-disable-next-line no-console
			console.error(`An error occurred while loading the component; ${e}`);
		});
	});

	return (
		<div className={cx('swiper-container', props.className, styles.carousel)}>
			<div className="swiper-wrapper">
				{reviews.map((r) => (
					<figure key={r.key} className={cx('swiper-slide', styles.carouselItem)}>
						<blockquote>
							<p>{r.quote}</p>
							<footer className={styles.footer}>
								<Icon size="large">
									<QuoteIcon />
								</Icon>
								<figcaption className={styles.authorContainer}>
									<span className={styles.author}>&nbsp;{r?.buyerName}</span>
									<cite>{r.dealership}</cite>
								</figcaption>
							</footer>
						</blockquote>
					</figure>
				))}
			</div>
			<div className="swiper-pagination" />
		</div>
	);
};
